'use client';

/**
 * `<DocumentPreviewModal />` — entry-point unic pentru previzualizarea oricărui
 * tip de document din SoftFactura (factură, NIR, chitanță, eFactură, OP, ...).
 *
 * Pattern: registry + plugin. Shell-ul e domain-agnostic; conținutul (acțiuni,
 * sidebar, edit form) vine 100% din `DocumentTypeConfig` rezolvat lazy din registry.
 *
 * Usage:
 * ```tsx
 * <DocumentPreviewModal
 *   type="invoice"
 *   documentId={previewId}
 *   onClose={() => setPreview(null)}
 *   onRefresh={() => qc.invalidateQueries({ queryKey: ['invoices'] })}
 * />
 * ```
 *
 * Performanță la scale (100k+ users):
 * - Config tip lazy-loaded (code split per tip)
 * - PDF viewer dynamic import (next/dynamic, ssr:false)
 * - Animation 200-300ms transitions, no jank
 * - Shell stateless (re-renders minim)
 * - Suspense boundary cu fallback skeleton
 */

import { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { DocumentPreviewPanel } from '@/shared/components/document-preview-panel/document-preview-panel';
import type { BannerState, BannerTone } from '@/shared/components/document-preview-panel/types';
import { previewLoadFailureNotice } from './load-failure-notice';
import { loadDocumentTypes } from './registry';
import type { DocumentTypeConfig, ViewerContext } from './registry-types';
import { usePermissions } from '@/features/auth/hooks/use-permissions';
import type { LucideIcon } from 'lucide-react';
import type { ViewSwitcher } from '@/shared/components/document-preview-panel/types';

interface DocumentPreviewModalProps {
  /**
   * Tipul de document — cheia în registry (ex. 'invoice', 'nir', 'receipt').
   * Opțional dacă se folosește `viewVariants` (care aduce propriul type/id).
   */
  type?: string | null;
  /** ID-ul documentului. `null` = închis. Opțional cu `viewVariants`. */
  documentId?: string | null;
  /** Callback la închidere (Esc, click backdrop, X). */
  onClose: () => void;
  /** Callback la refresh (după acțiune ce schimbă starea, ex. delete, status update). */
  onRefresh?: () => void;
  /**
   * Callback pentru swap document — modalul rămâne deschis cu noul ID.
   * Util pentru flow-uri ca Duplicate (vezi InvoiceConfig). Dacă lipsește,
   * config-urile pot face fallback la close + navigate.
   */
  onSwitchDocument?: (id: string) => void;
  /** Mod editare inițial (opțional — default false). */
  initialEditMode?: boolean;
  /**
   * Banner inițial — afișat când modalul se deschide ca rezultat al unei
   * acțiuni din pagină (ex. handleDuplicate). Modalul îl preia pe mount și
   * apoi banner-ul se auto-dismiss după 3s. Pentru flash-uri ulterioare din
   * modal folosește `ctx.flashBanner` din ViewerContext.
   */
  initialBanner?: BannerState | null;
  /**
   * Variante alternative ale aceluiași document (ex. recepția NIR vs factura
   * din e-Factura). Cu ≥2 intrări, modalul afișează un comutator segmentat în
   * toolbar și gestionează intern varianta activă. Opt-in: dacă lipsește,
   * modalul folosește `type`+`documentId` (comportament clasic).
   */
  viewVariants?: ViewVariant[];
  /** Cheia variantei afișate implicit (default: prima). */
  defaultVariantKey?: string;
}

/** O variantă de afișare în `DocumentPreviewModal` (un document + tipul lui). */
interface ViewVariant {
  key: string;
  label: string;
  icon?: LucideIcon;
  type: string;
  documentId: string;
}

export function DocumentPreviewModal({
  type,
  documentId,
  onClose,
  onRefresh,
  onSwitchDocument,
  initialEditMode = false,
  initialBanner = null,
  viewVariants,
  defaultVariantKey,
}: DocumentPreviewModalProps) {
  // Varianta(le) de afișat: explicit prin `viewVariants`, altfel varianta unică
  // derivată din `type`+`documentId` (comportamentul clasic, neschimbat).
  const variants: ViewVariant[] = useMemo(
    () =>
      viewVariants && viewVariants.length > 0
        ? viewVariants
        : type && documentId
          ? [{ key: '__single__', label: '', type, documentId }]
          : [],
    [viewVariants, type, documentId],
  );

  const [activeKey, setActiveKey] = useState<string | null>(null);
  const active =
    variants.find((v) => v.key === activeKey) ??
    variants.find((v) => v.key === defaultVariantKey) ??
    variants[0] ??
    null;

  // Configurile TUTUROR variantelor se rezolvă lazy și se țin în state, ca swap-ul
  // între variante să fie sincron (fără gap/flicker). Rezolvarea unui tip deja în
  // cache-ul registry-ului e ~instant.
  const [configs, setConfigs] = useState<Record<string, DocumentTypeConfig>>({});
  // onClose vine de obicei inline din pagină (nou la fiecare render) — ref-ul îl ține
  // la zi fără să reia încărcarea.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(
    () =>
      loadDocumentTypes(
        variants.map((v) => v.type),
        (type, resolved) => setConfigs((prev) => (prev[type] ? prev : { ...prev, [type]: resolved })),
        (err) => {
          // Fără config modalul n-ar apărea deloc: spunem de ce și închidem, ca pagina să nu
          // rămână cu un modal invizibil deschis (28.09.2026: „Vezi factura” nu făcea nimic,
          // fără niciun mesaj).
          const notice = previewLoadFailureNotice(err, navigator.onLine);
          toast.error(notice.title, {
            id: 'document-preview-load-failed',
            description: notice.description,
            duration: 15000,
            ...(notice.reload ? { action: { label: 'Reîncarcă', onClick: () => window.location.reload() } } : {}),
          });
          onCloseRef.current();
        },
      ),
    [variants],
  );

  // Animație: prima deschidere animă drawer-ul; swap-ul de variantă NU (instant).
  // openedRef persistă peste swap-uri fiindcă outer-ul nu se remontează.
  const openedRef = useRef(false);
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      openedRef.current = true;
    });
    return () => cancelAnimationFrame(raf);
  }, []);

  const config = active ? configs[active.type] ?? null : null;

  if (!active || !config) {
    // Tip încă în lazy-load (sau modal închis) → shell-ul gestionează `open: false`.
    return null;
  }

  const viewSwitcher: ViewSwitcher | null =
    variants.length > 1
      ? {
          options: variants.map((v) => ({ key: v.key, label: v.label, icon: v.icon })),
          activeKey: active.key,
          onChange: setActiveKey,
        }
      : null;

  return (
    <DocumentPreviewModalInner
      key={config.type}
      config={config}
      documentId={active.documentId}
      onClose={onClose}
      onRefresh={onRefresh}
      onSwitchDocument={onSwitchDocument}
      initialEditMode={initialEditMode}
      initialBanner={initialBanner}
      viewSwitcher={viewSwitcher}
      instant={openedRef.current}
    />
  );
}

/**
 * Inner component — separat ca să putem apela `config.useDocument` ca hook
 * (hook-urile au reguli stricte despre order/conditional). `config` e garantat
 * non-null aici.
 */
function DocumentPreviewModalInner({
  config,
  documentId,
  onClose,
  onRefresh,
  onSwitchDocument,
  initialEditMode,
  initialBanner,
  viewSwitcher,
  instant,
}: {
  config: DocumentTypeConfig;
  documentId: string;
  onClose: () => void;
  onRefresh?: () => void;
  onSwitchDocument?: (id: string) => void;
  initialEditMode: boolean;
  initialBanner: BannerState | null;
  viewSwitcher: ViewSwitcher | null;
  instant: boolean;
}) {
  const { data: doc, isLoading } = config.useDocument(documentId);
  const [editMode, setEditMode] = useState(initialEditMode);
  const [previewTemplateId, setPreviewTemplateId] = useState<string | undefined>(undefined);

  // Banner state — sursă duală: (a) initialBanner setat de pagina părinte la mount/swap,
  // (b) flashBanner apelat de acțiunile interne ale config-ului. Counter-ul asigură că
  // două flash-uri consecutive cu același label resetează timer-ul de auto-dismiss.
  const bannerCounter = useRef(0);
  const [banner, setBanner] = useState<BannerState | null>(() => {
    if (!initialBanner) return null;
    bannerCounter.current += 1;
    return { ...initialBanner, id: bannerCounter.current };
  });

  // Sincronizare initialBanner → state la swap document (când părintele setează banner pentru un nou ID)
  const lastInitialBannerRef = useRef<BannerState | null>(initialBanner);
  useEffect(() => {
    if (initialBanner !== lastInitialBannerRef.current) {
      lastInitialBannerRef.current = initialBanner;
      if (initialBanner) {
        bannerCounter.current += 1;
        setBanner({ ...initialBanner, id: bannerCounter.current });
      }
    }
  }, [initialBanner]);

  const flashBanner = (state: { label: string; tone: BannerTone }) => {
    bannerCounter.current += 1;
    setBanner({ ...state, id: bannerCounter.current });
  };

  // PDF fetch ca blob cu credentials (cookie auth e cross-origin → react-pdf
  // direct nu poate fetch-a). Gestionăm aici, central, ca să nu duplicăm logica
  // în fiecare config. Trebuie computed INAINTE de ctx ca să putem expune
  // pdfBlobUrl în context pentru acțiuni (Print refolosește blob-ul existent).
  const rawPdfUrl = doc ? config.getPdfUrl(doc, { templateId: previewTemplateId }) : null;
  const { pdfBlobUrl, pdfLoading: pdfBlobLoading, pdfError } = usePdfBlob(rawPdfUrl);
  const pdfUrl = pdfBlobUrl;

  const ctx: ViewerContext = useMemo(() => ({
    close: onClose,
    refresh: () => onRefresh?.(),
    openDocument: onSwitchDocument,
    toggleEdit: config.EditForm ? () => setEditMode((e) => !e) : undefined,
    editMode,
    flashBanner,
    pdfBlobUrl,
    previewTemplateId,
    setPreviewTemplateId,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [onClose, onRefresh, onSwitchDocument, config.EditForm, editMode, pdfBlobUrl, previewTemplateId]);

  // Hook order trebuie respectat: apelez useActions doar când doc e disponibil.
  // Trick: dau un mock empty object dacă doc lipsește — actions vor avea show=false.
  // Mai curat: folosesc `useActions` doar când doc e prezent printr-un componenta
  // separată sau guard. Aici aleg să apelez condiționat folosind un wrapper.
  const actions = useActionsSafe(config, doc, ctx);
  const title = doc ? config.getTitle(doc) : config.label;
  const subtitle = doc && config.getSubtitle ? config.getSubtitle(doc) : undefined;
  const statusBadge = doc && config.getStatusBadge ? config.getStatusBadge(doc) : null;

  // Sidebar din config sau placeholder loading
  const SidebarComponent = config.Sidebar;
  const sidebar = doc && SidebarComponent
    ? <SidebarComponent key="sidebar" document={doc} ctx={ctx} />
    : null;

  const PrintTemplateComponent = config.PrintTemplate;
  const printTemplate = doc && PrintTemplateComponent
    ? <PrintTemplateComponent key="print-template" document={doc} ctx={ctx} />
    : null;

  // Edit form (afișat în loc de PDF când editMode)
  // key={doc.id} forțează re-mount când userul face duplicate (openDocument swap-uiește
  // doc-ul în modal): formul recitește initialData din noul document, evitând 409
  // Conflict-ul de la trimiterea unei versionExpected stale (a originalului).
  const editForm = doc && config.EditForm && editMode
    ? <config.EditForm key={doc.id} document={doc} onSaved={() => { setEditMode(false); onRefresh?.(); }} onCancel={() => setEditMode(false)} />
    : null;

  return (
    <>
      {printTemplate}
      <DocumentPreviewPanel
      open={true}
      onClose={onClose}
      isLoading={isLoading}
      document={doc}
      pdfUrl={pdfUrl}
      pdfLoading={(isLoading && !doc) || pdfBlobLoading}
      pdfError={pdfError}
      toolbarActions={actions}
      sidebarSections={sidebar ? [sidebar] : []}
      width={config.width ?? 1180}
      getDocumentTitle={() => title}
      getDocumentSubtitle={subtitle ? () => String(subtitle) : undefined}
      getDocumentStatus={statusBadge ? () => statusBadge : undefined}
      banner={banner}
      onBannerDismiss={() => setBanner(null)}
      viewSwitcher={viewSwitcher}
      instant={instant}
    />
    </>
  );
}

/**
 * Hook pentru fetch PDF ca blob cu credentials. Cookie-urile de auth sunt
 * cross-origin (app.softfactura.ro → api.softfactura.ro) deci react-pdf direct
 * nu le poate trimite. Fetch manual + blob URL e necesar pentru toate viewers.
 *
 * Cleanup automat la unmount sau schimbare URL.
 */
function usePdfBlob(rawUrl: string | null): {
  pdfBlobUrl: string | null;
  pdfLoading: boolean;
  pdfError: string | null;
} {
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  useEffect(() => {
    if (!rawUrl) {
      setPdfBlobUrl((p) => { if (p) URL.revokeObjectURL(p); return null; });
      setPdfError(null);
      return;
    }
    let cancelled = false;
    setPdfLoading(true);
    setPdfError(null);
    fetch(`${rawUrl}${rawUrl.includes('?') ? '&' : '?'}t=${Date.now()}`, { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error(`PDF error (${res.status})`);
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        setPdfBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
        setPdfLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setPdfError(err?.message || 'Eroare la încărcarea PDF');
        setPdfLoading(false);
      });
    return () => { cancelled = true; };
  }, [rawUrl]);

  // Cleanup la unmount
  useEffect(() => {
    return () => {
      setPdfBlobUrl((p) => { if (p) URL.revokeObjectURL(p); return null; });
    };
  }, []);

  return { pdfBlobUrl, pdfLoading, pdfError };
}

/**
 * Wrapper care apelează `config.useActions` în siguranță chiar dacă `doc`
 * lipsește. Returnează lista vidă în lipsa documentului.
 */
function useActionsSafe(
  config: DocumentTypeConfig,
  doc: unknown | undefined,
  ctx: ViewerContext,
) {
  // Trebuie să apelăm hook-ul indiferent (regula hooks). Dăm un sentinel
  // empty object care semnalează lipsa documentului. Configurile trebuie să
  // gestioneze cazul `doc == null` (toate acțiunile cu `show: false` natural).
  const safeDoc = doc ?? ({} as never);
  const actions = config.useActions(safeDoc, ctx);
  // Acțiunile cu `permission` apar doar cu dreptul lor (un cont doar de citire vede doar descărcările / navigarea).
  const { can, canAny } = usePermissions();
  const permise = actions.filter((a) => !a.permission || (Array.isArray(a.permission) ? canAny(a.permission) : can(a.permission)));
  // Dacă nu avem doc, returnăm un array vid pentru ca toolbar-ul să fie gol
  return doc ? permise : [];
}
